import flow from "lodash/flow";
import groupBy from "lodash/groupBy";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import sumBy from "lodash/sumBy";
import DateTime from "utils/DateTime";
import Timespan from "utils/Timespan";

export default class OverlappingPlannedBreaks {
  static calculateOverlappingPlannedBreaks(line, startDate, endDate) {
    let overlappingPlannedBreaks = [];
    let breaks;

    if (line) {
      breaks = new OverlappingPlannedBreaks({
        line,
      });

      overlappingPlannedBreaks = breaks.getCombinedOverlappingPlannedBreaks(
        new Timespan(new Date(startDate), new Date(endDate)),
      );
    }

    return overlappingPlannedBreaks;
  }

  constructor({ line }) {
    this.daysWithPlannedBreaks = this.getDaysWithPlannedBreaks(line);
  }

  getDaysWithPlannedBreaks(line) {
    if (!line) return {};

    const theBreaks = line.plannedBreaks
      .map((plannedBreak) =>
        DateTime.DAYS_OF_WEEK.map((day) => {
          if (!plannedBreak[day]) return null;

          return this.addBreakForDay(plannedBreak, day);
        }),
      )
      .flat(2)
      .filter((obj) => obj);

    const groupedBreaks = groupBy(theBreaks, "day");

    return mapValues(groupedBreaks, (breaks) => breaks.map((aBreak) => aBreak.plannedBreak));
  }

  addBreakForDay(plannedBreak, day) {
    if (plannedBreak.startTime > plannedBreak.endTime) {
      const index = DateTime.DAYS_OF_WEEK.indexOf(day);
      const nextDay = DateTime.DAYS_OF_WEEK[(index + 1) % 7];

      return [
        {
          day,
          plannedBreak: {
            endsAt: "24:00",
            name: plannedBreak.name,
            startsAt: plannedBreak.startTime,
          },
        },
        {
          day: nextDay,
          plannedBreak: {
            endsAt: plannedBreak.endTime,
            name: plannedBreak.name,
            startsAt: "00:00",
          },
        },
      ];
    }
    return [
      {
        day,
        plannedBreak: {
          endsAt: plannedBreak.endTime,
          name: plannedBreak.name,
          startsAt: plannedBreak.startTime,
        },
      },
    ];
  }

  getOverlappingPlannedBreaks(workBlock, minimumRequiredProductiveDuration) {
    const oneMinute = 60000;
    const allOverlappingBreaks = [];
    const startsAt = new Date(workBlock.startsAt);
    let currentTimespan = this.buildTimespan(startsAt, minimumRequiredProductiveDuration);
    let unscheduledProductiveDuration = minimumRequiredProductiveDuration;

    while (unscheduledProductiveDuration >= oneMinute) {
      allOverlappingBreaks.push(...this.getOverlappingBreaks(currentTimespan));
      const totalOverlappingBreaksDuration = sumBy(allOverlappingBreaks, "duration");
      const totalScheduledDuration = currentTimespan.endTime - startsAt;
      const productiveDuration = totalScheduledDuration - totalOverlappingBreaksDuration;
      unscheduledProductiveDuration = minimumRequiredProductiveDuration - productiveDuration;
      currentTimespan = this.buildTimespan(currentTimespan.endTime, unscheduledProductiveDuration);
    }

    return this.combineBreaks(allOverlappingBreaks);
  }

  buildTimespan(startsAt, duration) {
    const startTime = new Date(startsAt);
    const endTime = DateTime.addDurationDate(startTime, duration, "milliseconds");

    return new Timespan(startTime, endTime);
  }

  getCombinedOverlappingPlannedBreaks(timespan) {
    const overlappingBreaks = this.getOverlappingBreaks(timespan);

    return this.combineBreaks(overlappingBreaks);
  }

  getOverlappingBreaks(timespan) {
    return timespan
      .splitByDay()
      .flatMap((aTimespan) => {
        const day = DateTime.dayOfWeekName(aTimespan.startTime).toLowerCase();
        const breaks = this.daysWithPlannedBreaks[day] || [];

        return breaks.map((plannedBreak) => this.buildOverlappingBreak(plannedBreak, aTimespan));
      })
      .filter((obj) => obj);
  }

  buildOverlappingBreak(plannedBreak, candidateTimespan) {
    const currentDateTime = candidateTimespan.startTime;
    const plannedBreakTimespan = Timespan.buildFromPlannedBreak({ plannedBreak, referenceDate: currentDateTime });
    const overlap = plannedBreakTimespan.intersection(candidateTimespan);
    if (!overlap) return null;
    const overlappingDuration = overlap.durationInMilliseconds();

    return {
      duration: overlappingDuration,
      plannedBreakName: plannedBreak.name,
    };
  }

  combineBreaks(overlappingBreaks) {
    return flow([
      (breaks) => groupBy(breaks, "plannedBreakName"),
      (grouped) =>
        map(grouped, (breaks, name) => ({
          duration: sumBy(breaks, "duration") / 1000,
          plannedBreakName: name,
        })),
    ])(overlappingBreaks);
  }
}
